import { useEffect, useState } from 'react'
import {
  ShieldCheck, History, ShieldAlert, FileCheck2, SlidersHorizontal,
  Scale, CheckCircle2
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { ApprovalCentre } from '@/components/approvals/ApprovalCentre'
import { Tier3OverridePanel } from '@/components/panels/Tier3OverridePanel'
import { ToolPermissionsPanel } from '@/components/panels/ToolPermissionsPanel'
import { useAppStore } from '@/store'
import { cn } from '@/lib/utils'

const POLICIES = [
  { name: 'Tiered Approval System', status: 'active', tier: '0-3', lastUpdated: '2026-06-11', detail: 'Controls human approval requirements across tool tiers.' },
  { name: 'Auth Fail-Closed Policy', status: 'active', tier: 'all', lastUpdated: '2026-06-11', detail: 'Authority checks fail closed when identity or approval state cannot be verified.' },
  { name: 'AI Code Review Mandate', status: 'active', tier: '2+', lastUpdated: '2026-06-11', detail: 'Requires governed review for material platform changes.' },
  { name: 'Knowledge Registry Logging', status: 'active', tier: 'all', lastUpdated: '2026-06-09', detail: 'Tracks governed knowledge changes and source provenance.' },
  { name: 'Deployment Binding Restore', status: 'active', tier: '3', lastUpdated: '2026-06-10', detail: 'Protects deployment binding continuity during governed releases.' },
  { name: 'Tier 3 Gate: Human-Only Control', status: 'active', tier: '3', lastUpdated: '2026-08-14', detail: 'Tier 3 authority remains explicitly human controlled.' },
]

type GovernanceTab = 'approvals' | 'history' | 'overrides' | 'policies' | 'permissions'

const TABS: Array<{ id: GovernanceTab; label: string; icon: typeof ShieldCheck }> = [
  { id: 'approvals', label: 'Approval Centre', icon: ShieldCheck },
  { id: 'history', label: 'Recent Decisions', icon: History },
  { id: 'overrides', label: 'Tier Overrides', icon: ShieldAlert },
  { id: 'policies', label: 'Active Policies', icon: FileCheck2 },
  { id: 'permissions', label: 'Tool Permissions', icon: SlidersHorizontal },
]

function initialTab(section: string): GovernanceTab {
  if (section === 'tier3override') return 'overrides'
  if (section === 'toolpermissions') return 'permissions'
  return 'approvals'
}

function ActivePoliciesTab() {
  return (
    <div className="ops-governance-surface rounded-2xl p-4 sm:p-5">
      <div className="flex items-center gap-3 mb-4">
        <span className="ops-icon-tile ops-icon-tile-group"><Scale className="h-5 w-5" /></span>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[17px] font-semibold text-text-primary">Active Policies</h3>
            <Badge variant="success">{POLICIES.length} active</Badge>
          </div>
          <p className="text-xs text-text-muted mt-1">Current governance rules applied across the OPS platform.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {POLICIES.map(policy => (
          <div key={policy.name} className="ops-policy-card">
            <div className="flex items-start gap-3">
              <span className="ops-policy-check"><CheckCircle2 className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-text-primary text-sm">{policy.name}</div>
                  <Badge variant="success">active</Badge>
                </div>
                <div className="text-[11px] text-text-muted mt-1 leading-relaxed">{policy.detail}</div>
                <div className="flex items-center gap-3 mt-3 text-[10px] text-text-muted">
                  <span>Tier scope: <strong className="text-text-secondary">{policy.tier}</strong></span>
                  <span>Updated {policy.lastUpdated}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function GovernancePanel() {
  const requestedSection = useAppStore(s => s.activeSection)
  const [tab, setTab] = useState<GovernanceTab>(() => initialTab(requestedSection))

  useEffect(() => {
    if (requestedSection === 'tier3override') setTab('overrides')
    if (requestedSection === 'toolpermissions') setTab('permissions')
  }, [requestedSection])

  return (
    <div className="ops-governance-workspace">
      <section className="ops-governance-hero">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[.22em] font-semibold text-accent">Governance</div>
          <h1 className="text-[25px] sm:text-[30px] font-semibold tracking-tight text-text-primary mt-1">
            Approval. Control. Security.
          </h1>
          <p className="text-xs sm:text-sm text-text-muted mt-2 max-w-2xl leading-relaxed">
            Manage approvals, review decisions, control temporary authority windows, inspect active policies,
            and govern tool permissions from one workspace.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[.20em] text-text-muted">
          <ShieldCheck className="h-5 w-5 text-accent" />
          Secure governance
        </div>
      </section>

      <div className="ops-governance-tab-rail" role="tablist" aria-label="Governance sections">
        {TABS.map(item => {
          const Icon = item.icon
          const active = tab === item.id
          return (
            <button
              key={item.id}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(item.id)}
              className={cn('ops-governance-tab', active && 'ops-governance-tab-active')}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
            </button>
          )
        })}
      </div>

      <div className="mt-4">
        {tab === 'approvals' && <ApprovalCentre mode="approvals" />}
        {tab === 'history' && <ApprovalCentre mode="history" />}
        {tab === 'overrides' && <Tier3OverridePanel embedded />}
        {tab === 'policies' && <ActivePoliciesTab />}
        {tab === 'permissions' && <ToolPermissionsPanel embedded />}
      </div>
    </div>
  )
}
